/**
 * New version nudge (12 Oct 2026; redesigned 17 Oct 2026). Every deploy gives the app's main script a new hashed name
 * and writes its version and "what's new" (whats-new.json) into index.html, so the page checks every 2 minutes (and
 * when the tab comes back into view) whether the live index.html points at a different script. If so, a capsule rises
 * from the bottom: the flame, "v3.63.0 is here", up to three lines on what changed, Update now and Later.
 *
 * - It never interrupts: while someone is typing or has a task open, it waits until they are done.
 * - Later hides it for 30 minutes. A release marked "required" (one the old page cannot work with) has no Later.
 * - Update now plays a short light sweep and reloads into the new version.
 * Nothing happens in development, where scripts are not hashed.
 */
import { useEffect, useState } from 'react';
import { TfMark } from '../taskforce/brand';

const EVERY_MS = 2 * 60_000;
const SNOOZE_MS = 30 * 60_000;
const VERSION = /<meta name="app-version" content="([^"]+)"/;
const NEWS = /<meta name="app-whats-new" content="([^"]*)"/;
const BUNDLE = /\/assets\/index-[\w-]+\.js/;

interface Next {
  version: string;
  required: boolean;
  notes: string[];
}

/** The main script this page was loaded with, e.g. /assets/index-AbC123.js. */
function current(): string | null {
  for (const s of document.querySelectorAll<HTMLScriptElement>('script[src]')) {
    const m = BUNDLE.exec(s.src);
    if (m) return m[0];
  }
  return null;
}

function parseNews(html: string): { required: boolean; notes: string[] } {
  const raw = NEWS.exec(html)?.[1];
  if (!raw) return { required: false, notes: [] };
  try {
    const text = new DOMParser().parseFromString(`<i>${raw}</i>`, 'text/html').body.textContent ?? '';
    const v = JSON.parse(text) as { required?: boolean; notes?: unknown };
    const notes = Array.isArray(v.notes) ? v.notes.filter((n): n is string => typeof n === 'string').slice(0, 3) : [];
    return { required: !!v.required, notes };
  } catch {
    return { required: false, notes: [] };
  }
}

/** Someone is mid-way through something: typing, or a task (or other dialog) is open. */
function busy(): boolean {
  const a = document.activeElement as HTMLElement | null;
  if (a && (a.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName))) return true;
  // Only dialogs on screen count (the report's drawer stays in the page while closed).
  return [...document.querySelectorAll<HTMLElement>('.tfmodal, .tfcreate, [aria-modal="true"]')].some(
    (el) =>
      el.getClientRects().length > 0 &&
      getComputedStyle(el).visibility !== 'hidden' &&
      !el.closest('[hidden], [inert], [aria-hidden="true"]'),
  );
}

const snoozeKey = (v: string) => `upd-later-${v}`;
function snoozed(v: string): boolean {
  try {
    return Date.now() < Number(localStorage.getItem(snoozeKey(v)) ?? 0);
  } catch {
    return false;
  }
}

const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

export function UpdatePrompt() {
  const [next, setNext] = useState<Next | null>(null);
  const [show, setShow] = useState(false);
  const [going, setGoing] = useState(false);
  useEffect(() => {
    const mine = current();
    if (!mine) return;
    let stop = false;
    const check = async () => {
      if (stop || document.visibilityState !== 'visible') return;
      try {
        const html = await (await fetch('/', { cache: 'no-store', headers: { Accept: 'text/html' } })).text();
        const live = BUNDLE.exec(html)?.[0];
        if (live && live !== mine) setNext({ version: VERSION.exec(html)?.[1] ?? '', ...parseNews(html) });
      } catch {
        // Offline or the server is waking up: try again next time.
      }
    };
    const t = setInterval(check, EVERY_MS);
    const onShow = () => void check();
    document.addEventListener('visibilitychange', onShow);
    window.addEventListener('focus', onShow);
    return () => {
      stop = true;
      clearInterval(t);
      document.removeEventListener('visibilitychange', onShow);
      window.removeEventListener('focus', onShow);
    };
  }, []);
  // Show once nobody is in the middle of something (a required update does not wait), and not while snoozed.
  useEffect(() => {
    if (!next || show) return;
    const tryShow = () => {
      if (!next.required && (snoozed(next.version) || busy())) return;
      setShow(true);
    };
    tryShow();
    const t = setInterval(tryShow, 2000);
    return () => clearInterval(t);
  }, [next, show]);
  if (!next || !show) return null;
  const update = () => {
    if (reducedMotion()) return window.location.reload();
    setGoing(true);
    setTimeout(() => window.location.reload(), 650);
  };
  const later = () => {
    try {
      localStorage.setItem(snoozeKey(next.version), String(Date.now() + SNOOZE_MS));
    } catch {
      // Not kept: it hides for this visit only.
    }
    setShow(false);
  };
  return (
    <>
      <div className="upd" role="status" aria-live="polite" aria-label="A new version is available">
        <span className="upd-orb" aria-hidden="true">
          <TfMark size={30} />
        </span>
        <div className="upd-body">
          <b className="upd-title">
            {next.version ? (
              <>
                <span className="upd-v">v{next.version}</span> is here
              </>
            ) : (
              'A new version is here'
            )}
          </b>
          {next.notes.length > 0 ? (
            <ul className="upd-notes">
              {next.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          ) : (
            <span className="upd-sub">Update to get the latest. Your filters stay as they are.</span>
          )}
          {next.required && <span className="upd-req">This update is needed to keep working.</span>}
        </div>
        <div className="upd-acts">
          {!next.required && (
            <button type="button" className="upd-later" onClick={later}>
              Later
            </button>
          )}
          <button type="button" className="upd-go" onClick={update}>
            Update now
          </button>
        </div>
      </div>
      {going && <div className="upd-warp" aria-hidden="true" />}
    </>
  );
}
